import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { marksApi, reportCardApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Progress } from '../../components/ui/progress';
import { FileText, Award, TrendingUp, Download } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const StudentMarksPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMarks();
  }, []);

  const loadMarks = async () => {
    try {
      const response = await marksApi.getStats(user?.student_id);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load marks:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-8 w-8 text-primary" />
      </div>
    );
  }

  // Group marks by exam type
  const examTypeData = (stats?.records || []).reduce((acc, mark) => {
    const existing = acc.find(e => e.name === mark.exam_type);
    if (existing) {
      existing.total += mark.total_marks;
      existing.obtained += mark.marks_obtained;
    } else {
      acc.push({ name: mark.exam_type, total: mark.total_marks, obtained: mark.marks_obtained });
    }
    return acc;
  }, []).map(e => ({
    ...e,
    percentage: Math.round((e.obtained / e.total) * 100)
  }));

  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A+': case 'A': return 'text-green-500';
      case 'B+': case 'B': return 'text-blue-500';
      case 'C': case 'D': return 'text-yellow-500';
      default: return 'text-red-500';
    }
  };

  return (
    <div className="space-y-6" data-testid="student-marks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">My Marks</h1>
          <p className="text-muted-foreground mt-1">
            View your academic performance. Report cards are issued by the school office.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg bg-primary/10">
                <TrendingUp className="h-6 w-6 text-primary" />
              </div>
              <span className={`text-3xl font-bold ${getGradeColor(stats?.grade)}`}>
                {stats?.percentage || 0}%
              </span>
            </div>
            <p className="text-sm text-muted-foreground">Overall Percentage</p>
            <Progress value={stats?.percentage || 0} className="mt-2" />
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg bg-secondary/10">
                <Award className="h-6 w-6 text-secondary" />
              </div>
              <span className={`text-3xl font-bold ${getGradeColor(stats?.grade)}`}>
                {stats?.grade || 'N/A'}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">Grade</p>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg bg-green-500/10">
                <FileText className="h-6 w-6 text-green-500" />
              </div>
              <span className="text-3xl font-bold">{stats?.obtained_marks || 0}/{stats?.total_marks || 0}</span>
            </div>
            <p className="text-sm text-muted-foreground">Marks Obtained</p>
          </CardContent>
        </Card>
      </div>

      {/* Performance Chart */}
      {examTypeData.length > 0 && (
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Performance by Exam Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={examTypeData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="name" className="text-muted-foreground" />
                  <YAxis domain={[0, 100]} className="text-muted-foreground" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value) => [`${value}%`, 'Score']}
                  />
                  <Bar dataKey="percentage" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Marks Table */}
      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Detailed Marks
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.records?.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Subject</TableHead>
                    <TableHead>Exam Type</TableHead>
                    <TableHead>Marks</TableHead>
                    <TableHead>Percentage</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.records.map((mark, index) => {
                    const percentage = Math.round((mark.marks_obtained / mark.total_marks) * 100);
                    return (
                      <TableRow key={index} className="table-row-hover">
                        <TableCell className="font-medium">{mark.subject_id}</TableCell>
                        <TableCell>{mark.exam_type}</TableCell>
                        <TableCell>{mark.marks_obtained}/{mark.total_marks}</TableCell>
                        <TableCell>
                          <span className={percentage >= 60 ? 'text-green-500' : 'text-red-500'}>
                            {percentage}%
                          </span>
                        </TableCell>
                        <TableCell>{mark.date || '-'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No marks available</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StudentMarksPage;
